import type { IAccount } from "@/modules/account/common/types";
import { accountTransferTemplateCommissionFieldKeys } from "@/modules/template/common/constants";
import type {
  IAccountTransferTemplateForm,
  IAccountTransferTemplateRequest
} from "@/modules/template/common/types";
import {
  useCreateAccountTransferTemplateMutation,
  useGetAccountTransferTemplateQuery,
  useUpdateAccountTransferTemplateMutation,
} from "@/modules/template/services";
import { TransferKeys } from "@/modules/transaction/common/enum";
import { getBeneficiaryLegalStatusOptions } from "@/modules/transaction/helpers";
import { useAccountTransfer } from "@/modules/transaction/hooks";
import { useLazyGetAccountTransferCommissionQuery } from "@/modules/transaction/services";
import { CURRENCY, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { AppPaths, BeneficiaryType, SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import {
  OwnAccountSelectOption,
  Select,
  TemplateLayout,
  TransferBeneficiaries
} from "@/shared/components";
import {
  cleanupAmount,
  getAmountOrNull,
  isAmeriaAccount,
  isValidAccount,
  isValidAmount
} from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  accountsList: IAccount[];
  isEdit?: boolean;
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const AccountTransferTemplateForm = ({
  accountsList,
  isEdit,
  setIsAccountNumberInvalid,
}: Props) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const idempotencyKey = useRef(uuid4()).current;
  const templateName = searchParams.get(SearchParamKeys.TemplateName);

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});
  const [beneficiaryName, setBeneficiaryName] = useState<string>("");

  const {
    reset,
    values,
    errors,
    setValue,
    accountData,
    fieldChanges,
    canGetCommission,
    handleClickTarget,
    accountsFilteredList,
    handleChangeBeneficiaryAccount,
  } = useAccountTransfer({
    accountsList,
    beneficiaryName,
    setIsAccountNumberInvalid,
    commissionFieldKeys: accountTransferTemplateCommissionFieldKeys,
  });

  const [getCommission] = useLazyGetAccountTransferCommissionQuery();
  const {
    data: accountTransferTemplate,
    isLoading: isGetTemplateLoading,
  } = useGetAccountTransferTemplateQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: !isEdit && !operationId
  });
  const [
    createAccountTransferTemplateApi,
    { isLoading: isCreateLoading },
  ] = useCreateAccountTransferTemplateMutation();
  const [
    updateAccountTransferTemplateApi,
    { isLoading: isUpdateLoading },
  ] = useUpdateAccountTransferTemplateMutation();

  const accountTransferTemplateAction = useCallback((values: IAccountTransferTemplateForm) => {
    const decimals = values.currency === CURRENCY.AMD.label ? 1 : 2;
    const requestData: IAccountTransferTemplateRequest = {
      ...values,
      name: templateName ?? "",
      amount: +(+cleanupAmount(`${values.amount}`)).toFixed(decimals) || null,
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount
      ),
      commissionAccount: values.commissionAccount ? accountsList?.find(({ number }) => {
        return number === values.commissionAccount;
      }) : undefined,
    };

    return isEdit ?
      updateAccountTransferTemplateApi({
        data: requestData,
        id: `${operationId}`
      }).unwrap() :
      createAccountTransferTemplateApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList]);

  const setDefaultValues = () => {
    const data = {
      creditAccount: accountTransferTemplate?.creditAccount,
      debitAccount: accountTransferTemplate?.debitAccount?.number,
      amount: getAmountOrNull(accountTransferTemplate?.amount),
      purpose: accountTransferTemplate?.purpose,
      beneficiary: accountTransferTemplate?.beneficiary,
      beneficiaryLegalStatus: accountTransferTemplate?.beneficiaryLegalStatus,
      commissionAccount: accountTransferTemplate?.commissionAccount?.number,
      currency: accountTransferTemplate?.debitAccount?.currency,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    creditAccount: values.creditAccount,
    debitAccount: values.debitAccount,
    amount: getAmountOrNull(values.amount),
    purpose: values.purpose,
    beneficiary: values.beneficiary,
    commissionAccount: values.commissionAccount,
    beneficiaryLegalStatus: values.beneficiaryLegalStatus,
    currency: values.currency,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if (isEdit && !!accountTransferTemplate?.transactionType) {
      handleChangeBeneficiaryAccount(accountTransferTemplate.creditAccount, {
        beneficiary: accountTransferTemplate?.beneficiary,
      });
      setDefaultValues();
    }
  }, [accountTransferTemplate]);

  useEffect(() => {
    if (!isEdit && !templateName) {
      navigate(AppPaths.Templates);
    }
  }, [isEdit, templateName]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.Purpose, t("transfer.account.purpose"));
    }
  }, [isEdit]);

  return (
    <TemplateLayout
      title={t("template.accountTransfer.title")}
      page="Account transfer template"
      getCommission={getCommission}
      accountsList={accountsList}
      isGetTemplateLoading={isGetTemplateLoading}
      isEdit={isEdit}
      isLoading={isCreateLoading || isUpdateLoading}
      hasChanges={hasChanges}
      canGetCommission={canGetCommission}
      fieldChanges={fieldChanges}
      saveTemplate={accountTransferTemplateAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <TransferBeneficiaries
                name="creditAccount"
                entryType={BeneficiaryType.Account}
                setBeneficiaryName={setBeneficiaryName}
                label={t("transfer.receiverAccount")}
                onChange={(v) => handleChangeBeneficiaryAccount(v)}
                placeholder={t("transfer.typeOrChooseAnAccount")}
              />
              {accountData?.bankName && (
                <Text className="mt-8" type="tertiary">
                  {accountData?.bankName}
                </Text>
              )}
              {isValidAccount(values.creditAccount) &&
                !isAmeriaAccount(values.creditAccount) && (
                  <FormField
                    name="beneficiaryLegalStatus"
                    className="mb-32 mt-32"
                    dataId="beneficiaryLegalStatus"
                    shouldValidate
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Select
                        {...props}
                        options={getBeneficiaryLegalStatusOptions({ t })}
                        label={t("transfer.beneficiaryLegalStatus")}
                        placeholder={t("transfer.chooseBeneficiaryLegalStatus")}
                      />
                    )}
                  />
                )}
              {accountData?.holder && isAmeriaAccount(values.creditAccount) ? (
                <div className="mt-32 flexbox justify-content--between">
                  <Text className="mr-16">{t("transfer.accountHolder")}</Text>
                  <Text className="text-align--end">{accountData?.holder}</Text>
                </div>
              ) : (
                <FormField
                  name="beneficiary"
                  className="mb-8 mt-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      required
                      isTrimValues
                      label={t("transfer.beneficiaryName")}
                      placeholder={t("transfer.enterBeneficiaryName")}
                    />
                  )}
                />
              )}
            </>,
            title: t("general.beneficiaryDetails"),
            value: "beneficiary_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                dataId="debitAccount"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    isSearchable
                    className="custom-select"
                    options={accountsFilteredList || []}
                    label={t("transfer.debitAccount")}
                    placeholder={t("transfer.chooseDebitAccount")}
                    handleChange={(val) => handleClickTarget(val as SingleValue<TOption>)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="amount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    label={t("general.amount")}
                    placeholder={t("general.enterAmount")}
                    thousandSeparator=","
                    decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                    rightIconProps={{
                      Component: CURRENCY[values.currency]?.symbolIcon || "",
                    }}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-32"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          }
        ]}
      />
    </TemplateLayout>
  );
};
